import { getAccessTokenSync } from '@/services/identity-client'

import type {
  TransformRequestHandler,
  TransformRequestResponse,
} from '@onxmaps/map-x'

/**
 * Map-X transform request handler for use with `MapManager` and `MapViewer` implementations.
 *
 * This function is designed to be passed as the `transformRequest` option when creating
 * `MapManager` instances or `MapViewer` implementations (MapViewerMapbox, MapViewerOnyx, MapViewerMaplibre, etc.).
 *
 * Usage example:
 * ```typescript
 * const mapManager = new MapManager({
 *   transformRequest: mapXTransformRequest,
 *   // other options...
 * });
 * ```
 *
 * @param url - URL object (pre-parsed by map-x viewer packages)
 * @param _resourceType - Reserved for future viewer-specific resource type handling
 * @returns Object containing URL and optional headers
 */
export const mapXTransformRequest: TransformRequestHandler = (
  url: URL,
  _resourceType?: string
) => {
  const response: TransformRequestResponse = {
    url,
  }

  if (response.url.hostname?.includes('onxmaps.com')) {
    response.headers = {
      Authorization: `Bearer ${getAccessTokenSync()}`,
      'ONX-Application-Id': 'fish',
      'ONX-Application-Platform': 'web',
      'ONX-Application-Version': process.env.SHORT_SHA || '',
    }
  }

  return response
}

/**
 * Bridge transform request handler for current Mapbox implementation.
 * Adapts the map-x transform function to work with Mapbox's string-based transform signature.
 *
 * This function is designed to be passed as the `transformRequest` option when creating direct mapbox-gl implementation
 *
 * @param url - URL string passed by mapbox-gl
 * @param _resourceType - Resource type (unused for now)
 * @returns Object containing URL string and optional headers, compatible with Mapbox RequestParameters
 */
export const transformRequest = (url: string, _resourceType?: string) => {
  try {
    const origin = window?.location?.origin
    const resolvedUrl = new URL(url, origin)
    const transform = mapXTransformRequest(resolvedUrl, _resourceType)
    return transform?.url
      ? { ...transform, url: transform.url.toString() }
      : { url }
  } catch {
    return { url }
  }
}
